import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';

import { AlertsComponent } from './admin/components/alerts/alerts.component';
import { SettingsComponent } from './admin/components/settings/settings.component';
import { HelpComponent } from './admin/components/help/help.component';
import { OrgChartComponent } from './admin/components/org-chart/org-chart.component';
import { ClientLandingPageComponent } from './admin/components/client-landing-page/client-landing-page.component';
import { EntityMgmtAppComponent } from './admin/components/entity-mgmt-app/entity-mgmt-app.component';
import { ControversyAppComponent } from './admin/components/controversy-app/controversy-app.component';
import { DashboardExecutiveComponent } from './admin/components/dashboard-executive/dashboard-executive.component';
import { FormViewComponent } from './admin/components/form-view/form-view.component';
import { EntityChartMapComponent } from './admin/components/entity-chart-map/entity-chart-map.component';
import { TaxCycleWorkflowComponent } from './admin/components/tax-cycle-workflow/tax-cycle-workflow.component';
import { PdfFormOrderComponent } from './admin/components/pdf-form-order/pdf-form-order.component';
import { BizzHierViewComponent } from './admin/components/bizz-hier-view/bizz-hier-view.component';
import { CalcsScenarioDataSetupComponent } from './foreign/components/calcs-scenario-data-setup/calcs-scenario-data-setup.component';
import { NewUpdateScreenComponent } from './new-update-screen/new-update-screen.component';
import { CalcsFilingGroupStatusComponent } from './foreign/components/calcs-filing-group-status/calcs-filing-group-status.component';
import { Section965TaxDbComponent } from './section965/components/section-965-tax-db/section-965-tax-db.component';

/**
 * Standalone top-level routes migrated from AngularJS ui-router.
 * These are NOT admin children -- they had their own top-level states in the original app.
 */
const routes: Routes = [
  // --- alerts ---
  { path: 'alerts', component: AlertsComponent, canActivate: [AuthGuard], data: { title: 'Alerts' } },

  // --- userSettings (user's own settings page, not admin userSettings) ---
  { path: 'settings', component: SettingsComponent, canActivate: [AuthGuard], data: { title: 'Settings' } },

  // --- help ---
  { path: 'help', component: HelpComponent, canActivate: [AuthGuard], data: { title: 'Help' } },

  // --- orgchart ---
  { path: 'orgchart', component: OrgChartComponent, canActivate: [AuthGuard], data: { title: 'Org Chart' } },

  // --- client-landing-page ---
  { path: 'client-landing-page', component: ClientLandingPageComponent, canActivate: [AuthGuard], data: { title: 'Client Selection' } },

  // --- entity-mgmt ---
  { path: 'entity-mgmt', component: EntityMgmtAppComponent, canActivate: [AuthGuard], data: { title: 'Entity Management' } },

  // --- controversy ---
  { path: 'controversy', component: ControversyAppComponent, canActivate: [AuthGuard], data: { title: 'Controversy' } },

  // --- executive-dashboards ---
  { path: 'executive-dashboards', component: DashboardExecutiveComponent, canActivate: [AuthGuard], data: { title: 'Executive Dashboards' } },
  { path: 'executive-dashboards-new', component: DashboardExecutiveComponent, canActivate: [AuthGuard], data: { title: 'Executive Dashboards', isNew: true } },

  // --- form-view (legacy ui-router parity from formViewTab.js) ---
  { path: 'form-view', redirectTo: 'form-view/dashboard', pathMatch: 'full' },
  { path: 'form-view/dashboard', component: FormViewComponent, canActivate: [AuthGuard], data: { title: 'Form View' } },
  { path: 'form-view/:id', redirectTo: 'form-view/dashboard', pathMatch: 'full' },
  { path: 'state-form-view', redirectTo: 'state-form-view/dashboard', pathMatch: 'full' },
  { path: 'state-form-view/dashboard', component: FormViewComponent, canActivate: [AuthGuard], data: { title: 'State Form View', isState: true } },

  // --- entity-chart-map ---
  { path: 'entity-chart-map', component: EntityChartMapComponent, canActivate: [AuthGuard], data: { title: 'Entity Chart Map' } },

  // --- tax-cycle-workflow ---
  { path: 'tax-cycle-workflow', component: TaxCycleWorkflowComponent, canActivate: [AuthGuard], data: { title: 'Tax Cycle Workflow' } },

  // --- pdf-form-order (top-level route, not admin child) ---
  { path: 'pdf-form-order', component: PdfFormOrderComponent, canActivate: [AuthGuard], data: { title: 'PDF Form Order' } },

  // --- legacy custom top-level states ---
  { path: 'business-hierarchy', component: BizzHierViewComponent, canActivate: [AuthGuard], data: { title: 'Business Hierarchy' } },
  { path: 'calcs-scenario-data-setup', component: CalcsScenarioDataSetupComponent, canActivate: [AuthGuard], data: { title: 'Calcs Scenario Data Setup' } },
  { path: 'new-update-screen', component: NewUpdateScreenComponent, canActivate: [AuthGuard], data: { title: 'New Update Screen' } },
  { path: 'calcs-filing-group-data-setup', component: CalcsFilingGroupStatusComponent, canActivate: [AuthGuard], data: { title: 'Calcs Filing Group Data Setup' } },

  // --- IRC Section 965 Tax Return (AngularJS parity: /#/irc_965_tax_return, /#/irc_965_tax_return_q1, /#/irc_965_tax_return_raf) ---
  { path: 'irc_965_tax_return', component: Section965TaxDbComponent, canActivate: [AuthGuard], data: { title: 'IRC Section 965 Tax Return' } },
  { path: 'irc_965_tax_return_q1', component: Section965TaxDbComponent, canActivate: [AuthGuard], data: { title: 'IRC Section 965 Tax Return Q1' } },
  { path: 'irc_965_tax_return_raf', component: Section965TaxDbComponent, canActivate: [AuthGuard], data: { title: 'IRC Section 965 Tax Return RAF' } },

  // --- Legacy efile (ui/app/components/efile/efile.js) state URLs ---
  // NOTE: submissions-dasboard[/list|/submission/:id/...] and migration-hist are
  // now served directly by EfileRoutingModule (no workspace wrapper) to match
  // the legacy top-level ui-router states. Only efile-data-push remains here
  // because its target custom element (`gtw-dst-efile-datapush-ce`) lives in
  // the DST module and is still wired through WorkspaceScreenComponent.
  { path: 'efile-data-push', redirectTo: 'workspace/efile-data-push', pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class StandaloneRoutesModule {}
